'use client';

import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { Alert } from '@//components/Alert';
import { Grid } from '@//components/Grid';
import { Heading } from '@//components/Heading';
import ProductCardContainer from '@//containers/ProductCardContainer';
import ProductCardSkeleton from '@//containers/ProductCardSkeleton';
import { UtilityBar } from '@//containers/UtilityBar';
import useVehicles from '@//data/search/vehicles/useVehicles';
import styles from './CampaignPage.module.scss';
import CampaignSort from './CampaignSort';
import Pagination from './Pagination';

const SKELETON_COUNT = 24;
const SkeletonList = new Array(SKELETON_COUNT).fill(null);

interface CampaignVehiclesProps {
  slug: string;
  heading?: string;
  lockedQuery: string;
  itemPage: string;
  currency: string;
}

const CampaignVehicles = ({
  slug,
  heading,
  lockedQuery,
  itemPage,
  currency,
}: CampaignVehiclesProps) => {
  const t = useTranslations('pages.search.results');
  const tSearch = useTranslations('pages.search');
  const urlSearchParams = useSearchParams();
  const offsetParam = urlSearchParams.get('offset');
  const sortParam = urlSearchParams.get('sort');

  const params = new URLSearchParams(lockedQuery);
  if (offsetParam && /^\d+$/.test(offsetParam)) {
    params.set('offset', offsetParam);
  }
  if (sortParam) {
    params.set('sort', sortParam);
  }

  const query = useVehicles(params);
  const { data, isFetching, isError, error } = query;
  const vehicles = data?.documentList?.documents ?? [];
  const pagination = data?.documentList?.pagination;
  const sortOptions = data?.documentList?.sortOptions;
  const totalHits = data?.stats?.totalHits ?? 0;

  const headingId = 'campaign-vehicles-heading';

  return (
    <>
      <section className={styles.productSection} aria-labelledby={headingId}>
        {heading && (
          <Heading level={2} size="xl" id={headingId}>
            {heading}
          </Heading>
        )}
        <UtilityBar label={tSearch('meta', { value: totalHits })}>
          <CampaignSort sortOptions={sortOptions} />
        </UtilityBar>
        {isError ? (
          <Alert id="campaign-vehicles-error" variant="critical" heading={t('error.heading')}>
            {t('error.message', {
              message: error?.message || t('error.unknown'),
            })}
          </Alert>
        ) : isFetching ? (
          <Grid
            columns={3}
            id="campaign-vehicles"
            role="region"
            aria-label={t('ariaLabel')}
            aria-busy="true"
            tabIndex={-1}
          >
            <div role="status" aria-live="polite" className="sr-only">
              {t('loading')}
            </div>
            {SkeletonList.map((_, i) => (
              <ProductCardSkeleton key={`campaign-vehicle-skeleton-${i}`} />
            ))}
          </Grid>
        ) : vehicles.length === 0 ? (
          <Alert id="campaign-vehicles-empty" heading={t('noResults.heading')}>
            {t('noResults.message')}
          </Alert>
        ) : (
          <Grid
            columns={3}
            id="campaign-vehicles"
            role="region"
            aria-label={t('ariaLabel')}
            tabIndex={-1}
            data-testid="campaign-vehicles-grid"
          >
            {vehicles.map((vehicle, index) => (
              <ProductCardContainer
                key={vehicle._id}
                item={vehicle}
                index={index}
                itemPage={itemPage}
                currency={currency}
                testId={`campaign-vehicle-${index + 1}`}
                loading={index < 3 ? 'eager' : 'lazy'}
              />
            ))}
          </Grid>
        )}
      </section>
      <Pagination rootUrl={`/${slug}`} pagination={pagination} />
    </>
  );
};

export default CampaignVehicles;
